import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

const getSessionId = () => {
  let id = localStorage.getItem('msk_chat_session');
  if (!id) {
    id = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem('msk_chat_session', id);
  }
  return id;
};

// Lightweight **bold** renderer (no HTML injection) so Claude's markdown-style
// emphasis shows up as actual bold text instead of literal asterisks.
const renderLite = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

export default function ChatWidget() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open || messages.length > 0) return;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/history/${sessionId.current}`);
        const data = await res.json();
        if (data.messages?.length) {
          setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
        } else {
          setMessages([{ role: 'assistant', content: t('chat.greeting') }]);
        }
      } catch {
        setMessages([{ role: 'assistant', content: t('chat.greeting') }]);
      }
    })();
  }, [open, messages.length, t]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setSending(true);
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((prev) => {
          const next = [...prev];
          next[next.length - 1] = { role: 'assistant', content: acc };
          return next;
        });
      }
    } catch {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: 'assistant', content: t('chat.error') };
        return next;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label={t('chat.bubbleLabel')}
          data-testid="chat-widget-open-button"
          className="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-50 w-14 h-14 rounded-full bg-[#1A1A1A] text-gold flex items-center justify-center shadow-2xl hover:bg-gold hover:text-[#1A1A1A] transition-all duration-500 hover:scale-105"
        >
          <MessageCircle className="w-6 h-6" strokeWidth={1.4} />
        </button>
      )}

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[380px] h-[70vh] sm:h-[560px] max-h-[600px] bg-ivory border border-[#EAE4D8] shadow-2xl flex flex-col animate-fade-up"
        >
          <div className="flex items-center justify-between px-5 py-4 bg-[#1A1A1A] text-white">
            <div>
              <div className="text-[13px] tracking-luxury uppercase text-gold">{t('chat.title')}</div>
              <div className="text-[11px] text-white/60 mt-0.5">{t('chat.subtitle')}</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label={t('chat.close')}
              data-testid="chat-widget-close-button"
              className="text-white/70 hover:text-gold transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} data-testid="chat-widget-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-white">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user' ? 'bg-[#1A1A1A] text-white' : 'bg-cream text-[#333] border border-[#EAE4D8]'
                  }`}
                >
                  {m.content ? renderLite(m.content) : (sending && i === messages.length - 1 ? t('chat.typing') : '')}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-[#EAE4D8] bg-ivory flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder={t('chat.placeholder')}
              data-testid="chat-widget-input"
              className="flex-1 px-4 py-3 text-sm bg-white border border-[#EAE4D8] focus:outline-none focus:border-gold"
            />
            <button
              onClick={handleSend}
              disabled={sending || !input.trim()}
              aria-label={t('chat.send')}
              data-testid="chat-widget-send-button"
              className="w-11 h-11 flex-shrink-0 flex items-center justify-center bg-[#1A1A1A] text-gold hover:bg-gold hover:text-[#1A1A1A] transition-colors disabled:opacity-40"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
